// F-106 (intégrité), F-108 (sessions, règles), F-109 (état du service, erreurs).
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { post } from "@core/api";
import { PublicShell } from "@core/layout";
import { dateTime } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { useSession } from "@core/session";
import { Badge, Button, Card, Empty, ErrorBox, KeyValues, Loading, Notice, PageHeader, StatusChip, Table, Tabs, useToast } from "@core/ui";
import { Mode, useAction } from "./shared";

type Tab = "regles" | "integrite" | "etat";

export function OrgSecurityPage() {
  const [tab, setTab] = useState<Tab>("regles");
  return (
    <div className="page">
      <PageHeader eyebrow="Espace" title="Sécurité" intro="Règles appliquées par le serveur, intégrité des pièces d’origine et état du service." />
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: "regles", label: "Règles et cloisonnement" },
          { value: "integrite", label: "Intégrité des originaux" },
          { value: "etat", label: "État du service" },
        ]}
      />
      {tab === "regles" ? <RulesTab /> : null}
      {tab === "integrite" ? <IntegrityTab /> : null}
      {tab === "etat" ? <StatusTab /> : null}
    </div>
  );
}

function RulesTab() {
  const org = useOrg();
  const q = useGet<any>("/api/v1/me/sessions");
  return (
    <div className="grid-2">
      <Card title="Règles appliquées">
        {q.data ? (
          <ul className="list-plain small">
            {(q.data.rules as string[]).map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        ) : (
          <Loading />
        )}
      </Card>
      <Card title="Accès à cet espace">
        <p className="small">Les droits de chaque membre se règlent dans « Membres et droits ». Les consultations de pièces sont visibles dans « Données personnelles &gt; Accès aux pièces ».</p>
        <div className="row">
          <Link to={org.to("parametres/membres")}>Membres et droits</Link>
          <Link to={org.to("donnees-personnelles")}>Accès aux pièces</Link>
          <Link to="/moi/securite">Mes sessions</Link>
        </div>
        <Notice tone="attention">La double authentification (MFA) n’est pas encore disponible.</Notice>
      </Card>
    </div>
  );
}

const INTEGRITY_LABELS: Record<string, string> = {
  empreinte_differente: "Empreinte différente de l’original",
  fichier_absent: "Fichier absent du stockage",
  illisible: "Fichier illisible (clé ou fichier altéré)",
};

function IntegrityTab() {
  const org = useOrg();
  const q = useGet<any>(org.api("/integrity-checks"));
  const invalidate = useInvalidate();
  const action = useAction();
  const run = async () => {
    const r = await action.run(() => post(org.api("/integrity-checks")));
    if (r) invalidate(org.api("/integrity-checks"));
  };
  const last = q.data?.items?.[0];
  return (
    <Card
      title="Intégrité des pièces d’origine"
      actions={
        org.can("operate") ? (
          <Button variant="secondaire" small busy={action.busy} onClick={run}>
            Contrôler maintenant
          </Button>
        ) : null
      }
    >
      <p className="small">
        Chaque pièce est conservée chiffrée avec son empreinte SHA-256 calculée au dépôt. Le contrôle relit chaque original et compare son empreinte : une différence signale une altération ou une perte à examiner, pas une conclusion sur le document.
      </p>
      <ErrorBox error={action.error || q.error} />
      {!last ? (
        <Empty title="Aucun contrôle effectué." />
      ) : (
        <div className="stack-sm">
          <KeyValues
            items={[
              ["Dernier contrôle", `${dateTime(last.started_at)} par ${last.started_by_name ?? "—"}`],
              ["Pièces contrôlées", last.total],
              ["Conformes", last.conformes],
              ["À examiner", last.anomalies],
            ]}
          />
          {last.anomalies === 0 ? (
            <StatusChip status="conforme" label="Toutes les empreintes correspondent" />
          ) : (
            <Table caption="Pièces à examiner">
              <thead>
                <tr>
                  <th>Pièce</th>
                  <th>Constat</th>
                  <th>Empreinte attendue</th>
                </tr>
              </thead>
              <tbody>
                {last.details.map((a: any) => (
                  <tr key={a.document_id}>
                    <td>
                      {a.titre}
                      <span className="block small muted">{a.categorie}</span>
                    </td>
                    <td>
                      <StatusChip status="anomalie" label={INTEGRITY_LABELS[a.statut] ?? a.statut} />
                    </td>
                    <td className="mono small">{String(a.empreinte_attendue).slice(0, 16)}…</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      )}
    </Card>
  );
}

function StatusTab() {
  const org = useOrg();
  const s = useGet<any>("/api/v1/system/status");
  const e = useGet<any>(org.api("/operations/errors"));
  return (
    <div className="stack">
      <Card title="État du service" actions={<Button variant="discret" small onClick={() => s.refetch()}>Actualiser</Button>}>
        {s.isLoading ? <Loading /> : null}
        <ErrorBox error={s.error} />
        {s.data ? (
          <div className="stack-sm">
            <p>{s.data.status === "ok" ? <StatusChip status="conforme" label="Service opérationnel" /> : <StatusChip status="anomalie" label="Service dégradé" />}</p>
            <KeyValues
              items={[
                ["Vérifié le", dateTime(s.data.checked_at)],
                ["Version", <span className="mono">{s.data.version}</span>],
                ["Environnement", s.data.environment],
                ["Base de données", `${s.data.database.ok ? "Disponible" : "Indisponible"} · ${s.data.database.latency_ms} ms · migration ${s.data.database.migrations?.derniere ?? "—"}`],
                ["Stockage chiffré", s.data.storage.ok ? "Écriture et lecture vérifiées" : "Échec de la sonde"],
                ["Sauvegardes", s.data.backups],
              ]}
            />
            <Table caption="Services tiers">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Fournisseur</th>
                  <th>Mode</th>
                </tr>
              </thead>
              <tbody>
                {s.data.services.map((x: any) => (
                  <tr key={x.service}>
                    <td>{x.service}</td>
                    <td className="mono small">{x.nom}</td>
                    <td>
                      <Mode mode={x.mode} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : null}
      </Card>
      <Card title="Erreurs récentes (30 jours)">
        <ErrorBox error={e.error} />
        {e.data?.items?.length === 0 ? <Empty title="Aucune erreur enregistrée." /> : null}
        {e.data?.items?.length ? (
          <Table caption="Erreurs récentes">
            <thead>
              <tr>
                <th>Date</th>
                <th>Origine</th>
                <th>Détail</th>
              </tr>
            </thead>
            <tbody>
              {e.data.items.map((x: any, i: number) => (
                <tr key={i}>
                  <td>{dateTime(x.at)}</td>
                  <td>
                    <Badge tone="erreur">{x.source.replace(/_/g, " ")}</Badge>
                    <span className="block small">{x.label}</span>
                  </td>
                  <td className="small">{x.detail}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
        {e.data ? <p className="small muted">{e.data.note}</p> : null}
      </Card>
    </div>
  );
}

/** /moi/securite : sessions actives, historique, règles. */
export function AccountSecurityPage() {
  const q = useGet<any>("/api/v1/me/sessions");
  const ev = useGet<any>("/api/v1/me/security-events");
  const invalidate = useInvalidate();
  const toast = useToast();
  const nav = useNavigate();
  const { refresh } = useSession();
  const action = useAction();
  const revoke = async (id: string) => {
    const r = await action.run(() => post<any>(`/api/v1/me/sessions/${id}/revoke`));
    if (!r) return;
    if (r.current) {
      await refresh();
      nav("/connexion");
      return;
    }
    toast("Session fermée.");
    invalidate("/api/v1/me/s");
  };
  const revokeOthers = async () => {
    const r = await action.run(() => post<any>("/api/v1/me/sessions/revoke-others"));
    if (r) {
      toast(`${r.revoked} session(s) fermée(s).`);
      invalidate("/api/v1/me/s");
    }
  };
  return (
    <PublicShell wide>
      <div className="stack">
        <p className="small">
          <Link to="/moi">Mon espace personnel</Link>
        </p>
        <h1>Sécurité de mon compte</h1>
        <ErrorBox error={action.error || q.error} />
        <Card
          title="Sessions"
          actions={
            <Button variant="secondaire" small busy={action.busy} onClick={revokeOthers}>
              Fermer mes autres sessions
            </Button>
          }
        >
          {q.isLoading ? <Loading /> : null}
          {q.data ? (
            <Table caption="Sessions">
              <thead>
                <tr>
                  <th>Appareil</th>
                  <th>Ouverte le</th>
                  <th>Dernière activité</th>
                  <th>État</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {q.data.items.map((s: any) => (
                  <tr key={s.id}>
                    <td>
                      {s.device}
                      {s.ip ? <span className="block small muted mono">{s.ip}</span> : null}
                    </td>
                    <td>{dateTime(s.created_at)}</td>
                    <td>{dateTime(s.last_seen_at)}</td>
                    <td>{s.current ? <Badge tone="encre">Cette session</Badge> : s.active ? <Badge tone="succes">Active</Badge> : <Badge>{s.revoked_at ? "Fermée" : "Expirée"}</Badge>}</td>
                    <td className="right">
                      {s.active ? (
                        <Button variant="discret" small busy={action.busy} onClick={() => revoke(s.id)}>
                          {s.current ? "Me déconnecter" : "Fermer"}
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}
        </Card>
        <div className="grid-2">
          <Card title="Historique de sécurité">
            {ev.data?.items?.length ? (
              <ul className="list-plain small">
                {ev.data.items.map((e: any, i: number) => (
                  <li key={i}>
                    {dateTime(e.created_at)} · {e.label}
                  </li>
                ))}
              </ul>
            ) : (
              <Empty title="Aucun événement." />
            )}
          </Card>
          <Card title="Règles appliquées">
            <ul className="list-plain small">
              {(q.data?.rules ?? []).map((r: string) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </PublicShell>
  );
}
